import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useReducedMotion } from "motion/react";
import { ArrowRight } from "@phosphor-icons/react";
import { PageFrame, Seo } from "../../kit.jsx";
import "./stories.css";

export function StNotFound({ back = "/stories", label = "Back to stories" }) {
  return (
    <PageFrame className="rd-page st-page">
      <Seo title="Page not found" path="/404" description="This Project GRID page could not be found." />
      <main className="st-missing">
        <p className="st-mono">Not found</p>
        <h1>This record is not in the archive.</h1>
        <p>The link may be old, or the page may have moved.</p>
        <Link className="gh-btn gh-btn--fill" to={back}>
          <span className="roll" data-label={label}><span>{label}</span></span>
          <ArrowRight weight="bold" aria-hidden="true" />
        </Link>
      </main>
    </PageFrame>
  );
}

export function RollLabel({ children }) {
  return <span className="roll" data-label={children}><span>{children}</span></span>;
}

// At most one inline video plays at a time outside the lightbox.
let activeInline = null;

export function playInline(video) {
  if (!video) return;
  if (activeInline && activeInline !== video) activeInline.pause();
  activeInline = video;
  const attempt = video.play();
  if (attempt && typeof attempt.catch === "function") attempt.catch(() => {});
}

export function pauseInline(video) {
  if (!video) return;
  video.pause();
  if (activeInline === video) activeInline = null;
}

export function pauseAllInline() {
  if (activeInline) activeInline.pause();
  activeInline = null;
}

export function formatDuration(seconds) {
  const total = Math.floor(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

// A silent clip that plays only while it is on screen, and never alongside another inline clip.
export function InlineClip({ clip, className = "", caption = true }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    const video = ref.current;
    if (!video || reduce) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) playInline(video);
        else pauseInline(video);
      },
      { threshold: 0.6 },
    );
    observer.observe(video);
    return () => {
      observer.disconnect();
      pauseInline(video);
    };
  }, [reduce]);
  return (
    <figure className={`st-clip ${className}`}>
      <div className="st-clip__frame" style={{ aspectRatio: `${clip.width} / ${clip.height}` }}>
        <video
          ref={ref}
          src={clip.src}
          poster={clip.poster}
          width={clip.width}
          height={clip.height}
          muted
          loop
          playsInline
          preload="none"
          controls={reduce}
          aria-label={clip.alt}
        />
      </div>
      {caption && <figcaption><span className="st-mono">Silent clip · {formatDuration(clip.duration)}</span> {clip.caption}</figcaption>}
    </figure>
  );
}
